/// <amd-module name="DistributedTaskUI/Logs/PhaseSummaryView" />
import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/PhaseSummaryView";

import * as React from "react";
import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import { getTaskAgentClient } from "DistributedTask/Client/RestClient/TaskAgent";

import { LogsTelemetryHelper } from "DistributedTaskUI/Logs/LogsTelemetryHelper";
import { LogsViewUtility } from "DistributedTaskUI/Logs/LogsViewUtility";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { MenuButton, IMenuItem } from "VSSUI/Menu";
import { Link } from "VSSUI/Link";
import { SpinnerSize, Spinner } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";

import { IVssNavigationService } from "VSS/Platform/Navigation";
import { localeFormat } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Duration } from "VSSUI/Duration";

const LogsViewUtility2 = LogsViewUtility; // This is for decreasing size of the minified script

export interface IPhaseSummaryViewProps {
    name: string;
    startTime: Date;
    finishTime?: Date;
    queueTime?: Date;
    logUrl?: string;
    cssClass?: string;
    agentName?: string;
    queueId?: number;
    onRenderSecondaryDetailsLeftSection?: () => JSX.Element;
    onRenderAuxiliaryDetailsRightSection?: () => JSX.Element;
    pageContext?: IVssPageContext;
}

export interface IPhaseSummaryViewState {
    fetchQueueInProgress?: boolean;
    queueName?: string;
    queueUrl?: string;
}

export class PhaseSummaryView extends VssComponent<IPhaseSummaryViewProps, IPhaseSummaryViewState> {

    public componentDidMount(): void {
        this._fetchQueueDetails();
    }

    public render(): React.ReactNode {
        return (
            <div className={css(this.props.cssClass, "phase-summary-container")}>
                {this._getPrimaryDetailsRow()}
                {this._getSecondaryDetailsRow()}
            </div>
        );
    }

    public componentDidUpdate(prevProps: IPhaseSummaryViewProps): void {
        if (prevProps.queueId !== this.props.queueId) {
            this._fetchQueueDetails();
        }
    }

    private _getPrimaryDetailsRow(): JSX.Element {
        return (
            <div className="primary-details-row flex">
                {this._getPhaseName()}
                <div className="time-details-section flex">
                    {this.props.startTime && this._getStartTimeSection()}
                </div>
            </div>
        );
    }

    private _getSecondaryDetailsRow(): JSX.Element {
        return (
            <div className="secondary-details-row flex">
                {this._renderSecondaryRowLeftSection()}
                {this._getSecondaryRowRightSection()}
            </div>
        );
    }

    private _renderSecondaryRowLeftSection(): JSX.Element {
        let leftSection: JSX.Element;
        if (this.props.onRenderSecondaryDetailsLeftSection) {
            leftSection = this.props.onRenderSecondaryDetailsLeftSection();
        } else {
            leftSection = (this.state && this.state.fetchQueueInProgress) ?
                this._getQueueLoadingComponent() :
                this._getAgentDetailsSection();
        }

        return (<div className="secondary-row-left-section">{leftSection}</div>);
    }

    private _getQueueLoadingComponent(): JSX.Element {
        return (
            <div className="queue-loading-component flex">
                <div className="queue-loading-spinner">
                    <Spinner
                        className={"queue-fetch-spinner"}
                        size={SpinnerSize.xSmall}
                    />
                </div>
                <div className="queue-loading-text">{Resources.FetchingAgentInformationText}</div>
            </div>
        );
    }

    private _getPhaseName(): JSX.Element {
        return (
            <div className="phase-name-container">
                <Tooltip overflowOnly={true} text={this.props.name}>
                    <div className={"phase-name ellipsis-overflow"} role="heading" aria-level={2}>{this.props.name}</div>
                </Tooltip>
            </div>
        );
    }

    private _getStartTimeSection(): JSX.Element {
        return (
            <div className="start-time-section">
                {localeFormat(Resources.PhaseSummaryStartTimeText, this.props.startTime.toLocaleString())}
            </div>
        );
    }

    private _getSecondaryRowRightSection(): React.ReactNode {
        let auxiliaryDetails: React.ReactNode = null;
        if (this.props.onRenderAuxiliaryDetailsRightSection) {
            auxiliaryDetails = this.props.onRenderAuxiliaryDetailsRightSection();
        }

        // Show contextual menu only when job has start time
        return (
            <div className="phase-summary-secondary-row-right-section flex">
                {auxiliaryDetails}
                {this.props.startTime && this._getContextualMenu()}
                {this.props.startTime && this._getDurationComponent()}
            </div>
        );
    }

    private _getAgentDetailsSection() {
        return (
            <div className="agent-details-section flex">
                {
                    this.state && this.state.queueName &&
                    <div className="queue-details flex">
                        <div className="queue-name-title">{Resources.QueueNameLabel}</div>
                        <Link
                            className={"queue-name-link ellipsis-overflow"}
                            href={this.state.queueUrl}
                            target="_blank"
                            rel={"noopener noreferrer"}
                            aria-label={this.state.queueName}
                            aria-describedby={"queue-name-description"}>

                            <div id="queue-name-description" className="hidden">{Resources.ViewQueueDescription}</div>
                            {this.state.queueName}

                        </Link>
                    </div>
                }
                {this.props.agentName && this.state && this.state.queueName && this._getSeparator()}
                {this.props.agentName &&
                    <div className="agent-details">
                        <div className="agent-name-section">{localeFormat(Resources.AgentName, this.props.agentName)}</div>
                    </div>
                }
            </div>
        );
    }

    private _getSeparator(): JSX.Element {
        return (
            <div className="phase-summary-separator">&middot;</div>
        );
    }

    private _getContextualMenu() {
        let overflowButtons = this._getOverFlowButtons();
        if (overflowButtons.length === 0) {
            return null;
        }

        return (
            <div className="phase-summary-ellipses-container">
                <MenuButton
                    subtle
                    hideDropdownIcon={true}
                    className="phase-summary-ellipsis-button"
                    iconProps={{ iconName: "More" }}
                    ariaLabel={Resources.MorePhaseSummaryOptionsEllipsesButtonAriaLabel}
                    contextualMenuProps={{
                        menuProps: {
                            id: "show-more-options-menu",
                            items: overflowButtons,
                        }
                    }}>
                </MenuButton>
            </div>
        );
    }

    private _getOverFlowButtons(): IMenuItem[] {

        const menuItems: IMenuItem[] = [];

        if (this.props.logUrl) {
            const downloadLogMenuItem: IMenuItem = {
                text: Resources.DownloadLogMenuitemLabel,
                id: "download-log",
                iconProps: { className: "bowtie-icon bowtie-transfer-download" },
                disabled: !this.props.logUrl,
                className: "fabric-style-overrides commandBar-hover-override show-logs",
                onActivate: this._onDownLoadLogClick
            };
            menuItems.push(downloadLogMenuItem);
        }

        return menuItems;
    }

    private _onDownLoadLogClick = (menuItem: IMenuItem, event: React.MouseEvent<HTMLButtonElement>) => {
        if (this.props.logUrl) {
            LogsViewUtility2.downloadLog(this.props.logUrl);

            LogsTelemetryHelper.publishDownloadLogsTelemetry(this.context.pageContext || this.props.pageContext, "PhaseLogDownload");
        }
    }

    private _getDurationComponent(): JSX.Element {
        return (
            <div className="phase-summary-clock-container" >
                {
                    <Duration
                        startDate={this.props.startTime}
                        endDate={this.props.finishTime}
                    />
                }
            </div>
        );
    }

    private _fetchQueueDetails(): void {
        const pageContext = (this.context as IVssComponentContext).pageContext || this.props.pageContext;
        if (!pageContext) {
            this.setState({ fetchQueueInProgress: false });
            return;
        }

        if (this.props.queueId && this.props.queueId > 0) {
            this.setState({ fetchQueueInProgress: true });

            if (!PhaseSummaryView._getAgentQueueCache[this.props.queueId]) {
                const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
                const project = navigationService.getCurrentRoute().routeValues.project || "";

                const taskAgentsClient = getTaskAgentClient(pageContext);
                PhaseSummaryView._getAgentQueueCache[this.props.queueId] = taskAgentsClient.getAgentQueue(this.props.queueId, project);
            }

            PhaseSummaryView._getAgentQueueCache[this.props.queueId].then(
                (queue: TaskAgentQueue) => {
                    if (queue) {
                        let queueUrl: string = LogsViewUtility2.getQueueUrl(queue.id, this.context.pageContext || this.props.pageContext);
                        this.setState({ fetchQueueInProgress: false, queueName: queue.name, queueUrl: queueUrl });
                    } else {
                        this.setState({ fetchQueueInProgress: false });
                    }
                },
                (error: any) => {
                    // eat any error and dont show the queue details
                    this.setState({ fetchQueueInProgress: false });
                });
        }
        else {
            this.setState({ fetchQueueInProgress: false });
        }
    }

    private static _getAgentQueueCache: { [key: string]: Promise<TaskAgentQueue> } = {};
}
